import React, { useEffect, useMemo, useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import "../assets/css/Issues.css";
import "../assets/css/Projects.css";
import "../assets/css/Permissions.css";
import "../assets/css/ViewCustomer.css";
import axiosInstance from "../components/api/axiosInstance";
import { fetchSubTasksByTaskId } from "./subTaskService";
import { fetchTasksByProject } from "./taskService";
import { createTestCase, extractCreatedTestCaseId } from "./issues/testCaseService";
import { ISSUE_CREATE_FORM_DRAFT_KEY } from "./issues/issueCreateFormDraft";
import { canPerformActionOnPage, PAGES, ACTIONS } from "../utils/roleAccess";

const STATUS_OPTIONS = ["Not Executed", "Passed", "Failed", "Blocked"];
const VALID_STATUSES = new Set(STATUS_OPTIONS);
const PRIORITY_OPTIONS = ["High", "Medium", "Low"];

function normalizeStatus(s) {
  if (!s || typeof s !== "string") return "Not Executed";
  const trimmed = s.trim();
  if (VALID_STATUSES.has(trimmed)) return trimmed;
  const lower = trimmed.toLowerCase();
  if (lower === "not run") return "Not Executed";
  const match = STATUS_OPTIONS.find((opt) => opt.toLowerCase() === lower);
  return match || "Not Executed";
}

function AddTestCase() {
  const navigate = useNavigate();
  const location = useLocation();
  const returnToPath = location.state?.returnTo;
  const fromIssueCreate = Boolean(location.state?.fromIssueCreate);

  const role = useMemo(() => {
    try {
      const u = localStorage.getItem("user");
      if (!u) return null;
      const user = JSON.parse(u);
      const primaryRole = Array.isArray(user.roles) && user.roles.length > 0 ? user.roles[0] : null;
      return (
        (primaryRole && (primaryRole.role_name || primaryRole.name || primaryRole.role)) ||
        user.role_name ||
        user.role ||
        null
      );
    } catch {
      return null;
    }
  }, []);
  const canCreate = canPerformActionOnPage(PAGES.ISSUES, ACTIONS.ADD, role);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [subprojects, setSubprojects] = useState([]);
  const [loadingSubprojects, setLoadingSubprojects] = useState(false);

  const [tasks, setTasks] = useState([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [subtasks, setSubtasks] = useState([]);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);

  const [form, setForm] = useState({
    projectId: "",
    subProjectId: "",
    taskId: "",
    subTaskId: "",
    title: "",
    description: "",
    preConditions: "",
    steps: "",
    expected: "",
    status: "Not Executed",
    priority: "Medium",
    comments: ""
  });

  useEffect(() => {
    const load = async () => {
      setLoadingProjects(true);
      try {
        const res = await axiosInstance.get("/projects");
        const raw = res.data;
        const arr = Array.isArray(raw) ? raw : raw?.data ?? raw?.projects ?? [];
        setProjects(arr || []);
      } catch (err) {
        console.error("Error fetching projects:", err);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!fromIssueCreate) return;
    try {
      const raw = sessionStorage.getItem(ISSUE_CREATE_FORM_DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw);
      const d = draft?.formData;
      if (!d || typeof d !== "object") return;
      setForm((prev) => ({
        ...prev,
        projectId: d.projectId != null ? String(d.projectId) : "",
        subProjectId: d.subProjectId != null ? String(d.subProjectId) : "",
        taskId: d.taskId != null ? String(d.taskId) : "",
        subTaskId: d.subTaskId != null ? String(d.subTaskId) : ""
      }));
    } catch {
      /* ignore */
    }
  }, [fromIssueCreate]);

  useEffect(() => {
    if (!form.projectId) {
      setSubprojects([]);
      return;
    }
    let cancelled = false;
    setLoadingSubprojects(true);
    axiosInstance
      .get(`/sub-projects/project/${form.projectId}`)
      .then((res) => {
        if (cancelled) return;
        const raw = res.data;
        let arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        const mapped = (arr || [])
          .map((sp) => ({
            id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
            name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
          }))
          .filter((x) => x.id != null && String(x.name).trim() !== "");
        setSubprojects(mapped);
        if (mapped.length === 0) {
          setForm((f) => ({ ...f, subProjectId: "" }));
        }
      })
      .catch(() => {
        if (!cancelled) setSubprojects([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingSubprojects(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.projectId]);

  const showSubprojectField =
    Boolean(form.projectId) && (loadingSubprojects ? true : subprojects.length > 0);

  useEffect(() => {
    if (!form.projectId) {
      setTasks([]);
      return;
    }
    if (loadingSubprojects) {
      setTasks([]);
      return;
    }

    let cancelled = false;
    const loadTasks = async () => {
      setLoadingTasks(true);
      try {
        const subId = showSubprojectField && form.subProjectId ? form.subProjectId : null;
        const list = await fetchTasksByProject(form.projectId, subId);
        if (cancelled) return;
        const mapped = (list || [])
          .map((t) => ({
            id: t.Task_Id ?? t.task_id ?? t.id,
            title: t.Title ?? t.title ?? t.Task_Title ?? t.task_title ?? "—"
          }))
          .filter((x) => x.id != null);
        mapped.sort((a, b) => String(a.title).localeCompare(String(b.title)));
        setTasks(mapped);
      } catch (err) {
        if (!cancelled) {
          console.error("Error loading tasks:", err);
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoadingTasks(false);
      }
    };
    loadTasks();
    return () => {
      cancelled = true;
    };
  }, [form.projectId, form.subProjectId, loadingSubprojects, showSubprojectField]);

  useEffect(() => {
    const loadSubtasks = async () => {
      const taskIdNum = Number(form.taskId);
      if (!taskIdNum || !Number.isFinite(taskIdNum)) {
        setSubtasks([]);
        setForm((p) => ({ ...p, subTaskId: "" }));
        return;
      }
      setLoadingSubtasks(true);
      try {
        const list = await fetchSubTasksByTaskId(taskIdNum);
        const mapped = (list || [])
          .map((st) => ({
            id: st.Sub_Task_Id ?? st.sub_task_id ?? st.id,
            title: st.Title ?? st.title ?? "—"
          }))
          .filter((x) => x.id != null);
        setSubtasks(mapped);
      } catch (err) {
        console.error("Error loading subtasks:", err);
        setSubtasks([]);
      } finally {
        setLoadingSubtasks(false);
      }
    };
    loadSubtasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.taskId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => {
      if (name === "projectId") {
        return {
          ...p,
          projectId: value,
          subProjectId: "",
          taskId: "",
          subTaskId: ""
        };
      }
      if (name === "subProjectId") {
        return { ...p, subProjectId: value, taskId: "", subTaskId: "" };
      }
      const next = { ...p, [name]: value };
      if (name === "taskId") next.subTaskId = "";
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canCreate) return;

    const projectId = Number(form.projectId);
    const taskId = Number(form.taskId);
    const subTaskId = form.subTaskId ? Number(form.subTaskId) : null;
    const title = (form.title || "").trim();
    if (!projectId || !Number.isFinite(projectId)) {
      setError("Project is required.");
      return;
    }
    if (!taskId || !title) {
      setError("Task and Title are required.");
      return;
    }

    let createdBy = 1;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const u = JSON.parse(userStr);
        createdBy = u.user_id || u.id || 1;
      }
    } catch {}

    const payload = {
      Task_Id: taskId,
      Sub_Task_Id: subTaskId,
      Title: title,
      Description: (form.description || "").trim() || null,
      Pre_Conditions: (form.preConditions || "").trim() || null,
      Steps: (form.steps || "").trim() || null,
      Expected_Result: (form.expected || "").trim() || null,
      Status: normalizeStatus(form.status),
      Priority: form.priority || "Medium",
      Comments: (form.comments || "").trim() || null,
      Created_By: createdBy
    };
    if (!payload.Sub_Task_Id) delete payload.Sub_Task_Id;

    setSubmitting(true);
    setError("");
    try {
      const created = await createTestCase(payload);
      const newTestCaseId = extractCreatedTestCaseId(created);
      const goBackToIssue = returnToPath && fromIssueCreate;

      if (goBackToIssue) {
        try {
          const raw = sessionStorage.getItem(ISSUE_CREATE_FORM_DRAFT_KEY);
          if (raw) {
            const draft = JSON.parse(raw);
            if (draft?.formData && typeof draft.formData === "object") {
              draft.formData = {
                ...draft.formData,
                testCaseId:
                  newTestCaseId != null ? String(newTestCaseId) : draft.formData.testCaseId || ""
              };
              sessionStorage.setItem(ISSUE_CREATE_FORM_DRAFT_KEY, JSON.stringify(draft));
            }
          }
        } catch {
          /* ignore */
        }
        alert("Test case created successfully.");
        navigate(returnToPath);
        return;
      }

      alert("Test case created successfully.");
      navigate("/issues/test-cases");
    } catch (err) {
      console.error("Error creating test case:", err);
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to create test case.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="issues-container">
      <div className="viewlead-header" style={{ marginBottom: 12 }}>
        <button
          type="button"
          className="viewlead-back-link"
          onClick={() => navigate(returnToPath || "/issues/test-cases")}
        >
          <FaArrowLeft /> {returnToPath ? "Back" : "Back"}
        </button>
      </div>

      <div className="issues-main-card">
        <form className="issues-form" onSubmit={handleSubmit}>
          <div className="issues-form-title">Create Test Case</div>

          {!canCreate ? (
            <div className="issues-alert">You don’t have permission to create test cases.</div>
          ) : null}
          {error ? <div className="issues-alert issues-alert-error">{error}</div> : null}

          <div className="issues-form-grid">
            <div className="form-group">
              <label>Project <span className="required">*</span></label>
              <select
                name="projectId"
                value={form.projectId}
                onChange={handleChange}
                required
                disabled={!canCreate || loadingProjects}
              >
                <option value="">
                  {loadingProjects ? "Loading projects..." : "Select project"}
                </option>
                {projects.map((p) => (
                  <option
                    key={p.Project_Id ?? p.project_id ?? p.id}
                    value={String(p.Project_Id ?? p.project_id ?? p.id)}
                  >
                    {p.Project_Name ?? p.project_name ?? p.name ?? ""}
                  </option>
                ))}
              </select>
            </div>

            {showSubprojectField ? (
              <div className="form-group">
                <label>Subproject</label>
                <select
                  name="subProjectId"
                  value={form.subProjectId}
                  onChange={handleChange}
                  disabled={!canCreate || loadingSubprojects}
                >
                  <option value="">Select subproject (optional)</option>
                  {subprojects.map((sp) => (
                    <option key={sp.id} value={String(sp.id)}>
                      {sp.name}
                    </option>
                  ))}
                </select>
              </div>
            ) : null}

            <div className="form-group">
              <label>Task <span className="required">*</span></label>
              <select
                name="taskId"
                value={form.taskId}
                onChange={handleChange}
                required
                disabled={!canCreate || loadingTasks || !form.projectId}
              >
                <option value="">
                  {!form.projectId
                    ? "Select project first"
                    : loadingTasks
                      ? "Loading tasks..."
                      : "Select Task"}
                </option>
                {tasks.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.title}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Sub Task</label>
              <select
                name="subTaskId"
                value={form.subTaskId}
                onChange={handleChange}
                disabled={!canCreate || !form.taskId || loadingSubtasks}
              >
                <option value="">
                  {loadingSubtasks ? "Loading subtasks..." : subtasks.length ? "No sub task" : "No sub task (select task)"}
                </option>
                {subtasks.map((st) => (
                  <option key={st.id} value={String(st.id)}>{st.title} (#{st.id})</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Title <span className="required">*</span></label>
              <input type="text" name="title" value={form.title} onChange={handleChange} required disabled={!canCreate} />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select name="status" value={form.status} onChange={handleChange} disabled={!canCreate}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label>Priority</label>
              <select name="priority" value={form.priority} onChange={handleChange} disabled={!canCreate}>
                {PRIORITY_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Pre Conditions</label>
              <input type="text" name="preConditions" value={form.preConditions} onChange={handleChange} disabled={!canCreate} />
            </div>

            <div className="form-group issues-description-full">
              <label>Description</label>
              <textarea name="description" value={form.description} onChange={handleChange} rows={3} disabled={!canCreate} />
            </div>
            <div className="form-group issues-description-full">
              <label>Steps</label>
              <textarea name="steps" value={form.steps} onChange={handleChange} rows={3} disabled={!canCreate} />
            </div>
            <div className="form-group issues-description-full">
              <label>Expected Result</label>
              <textarea name="expected" value={form.expected} onChange={handleChange} rows={2} disabled={!canCreate} />
            </div>
            <div className="form-group issues-description-full">
              <label>Comments</label>
              <textarea name="comments" value={form.comments} onChange={handleChange} rows={2} disabled={!canCreate} />
            </div>
          </div>

          <div className="issues-form-actions">
            <button type="submit" className="btn-add-lead" disabled={!canCreate || submitting}>
              {submitting ? "Creating..." : "Create Test Case"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddTestCase;


